import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const BookingModal = ({ venue, isOpen, onClose, onConfirm }) => {
  const [bookingData, setBookingData] = useState({
    date: '',
    startTime: '',
    duration: 1,
    playerCount: 10,
    notes: '',
    agreesToTerms: false,
    paymentMethod: 'pix'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Details, 2: Payment, 3: Confirmation

  const timeSlots = [
    '06:00', '07:00', '08:00', '09:00', '10:00', '11:00',
    '12:00', '13:00', '14:00', '15:00', '16:00', '17:00',
    '18:00', '19:00', '20:00', '21:00', '22:00'
  ];

  const durationOptions = [
    { value: 1, label: '1 hora' },
    { value: 1.5, label: '1h 30min' },
    { value: 2, label: '2 horas' },
    { value: 2.5, label: '2h 30min' },
    { value: 3, label: '3 horas' }
  ];

  const paymentMethods = [
    { value: 'pix', label: 'PIX', icon: 'Smartphone' },
    { value: 'credit', label: 'Cartão de Crédito', icon: 'CreditCard' },
    { value: 'debit', label: 'Cartão de Débito', icon: 'CreditCard' }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(amount);
  };

  const calculateTotal = () => {
    const basePrice = venue?.pricePerHour * bookingData?.duration;
    const serviceFee = basePrice * 0.05; // 5% service fee
    return basePrice + serviceFee;
  };

  const handleInputChange = (field, value) => {
    setBookingData(prev => ({ ...prev, [field]: value }));
  };

  const handleNextStep = () => {
    if (step < 3) {
      setStep(step + 1);
    }
  };

  const handlePrevStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleConfirmBooking = async () => {
    setIsLoading(true);
    
    // Simulate booking process
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const booking = {
      venue,
      ...bookingData,
      total: calculateTotal(),
      bookingId: `BK${Date.now()}`,
      status: 'confirmed'
    };
    
    setIsLoading(false);
    onConfirm(booking);
    onClose();
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return bookingData?.date && bookingData?.startTime && bookingData?.duration;
      case 2:
        return bookingData?.paymentMethod && bookingData?.agreesToTerms;
      case 3:
        return true;
      default:
        return false;
    }
  };

  if (!isOpen || !venue) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-elevation-4 w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-heading font-bold text-card-foreground">
              Reservar Quadra
            </h2>
            <p className="text-sm text-muted-foreground">
              {venue?.name} • {venue?.address}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-border">
          <div className="flex items-center justify-between">
            {[
              { number: 1, title: 'Detalhes', icon: 'Calendar' },
              { number: 2, title: 'Pagamento', icon: 'CreditCard' },
              { number: 3, title: 'Confirmação', icon: 'CheckCircle' }
            ]?.map((stepItem, index) => (
              <div key={stepItem?.number} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                  step >= stepItem?.number 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {step > stepItem?.number ? (
                    <Icon name="Check" size={16} />
                  ) : (
                    <Icon name={stepItem?.icon} size={16} />
                  )}
                </div>
                <span className={`ml-2 text-sm font-medium ${
                  step >= stepItem?.number ? 'text-foreground' : 'text-muted-foreground'
                }`}>
                  {stepItem?.title}
                </span>
                {index < 2 && (
                  <div className={`w-12 h-0.5 mx-4 ${
                    step > stepItem?.number ? 'bg-primary' : 'bg-muted'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Step 1: Booking Details */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Data da Reserva"
                  type="date"
                  value={bookingData?.date}
                  onChange={(e) => handleInputChange('date', e?.target?.value)}
                  min={new Date()?.toISOString()?.split('T')?.[0]}
                  required
                />
                
                <Select
                  label="Horário de Início"
                  options={timeSlots?.map(time => ({ value: time, label: time }))}
                  value={bookingData?.startTime}
                  onChange={(value) => handleInputChange('startTime', value)}
                  placeholder="Selecione o horário"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Duração"
                  options={durationOptions}
                  value={bookingData?.duration}
                  onChange={(value) => handleInputChange('duration', value)}
                />
                
                <Input
                  label="Número de Jogadores"
                  type="number"
                  value={bookingData?.playerCount}
                  onChange={(e) => handleInputChange('playerCount', parseInt(e?.target?.value))}
                  min="2"
                  max={venue?.capacity}
                />
              </div>

              <Input
                label="Observações (Opcional)"
                type="text"
                placeholder="Informações adicionais sobre a reserva"
                value={bookingData?.notes}
                onChange={(e) => handleInputChange('notes', e?.target?.value)}
              />

              {/* Price Preview */}
              <div className="bg-muted/50 rounded-lg p-4">
                <h3 className="font-medium text-foreground mb-3">Resumo do Valor</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Quadra ({bookingData?.duration}h)</span>
                    <span className="font-data">
                      {formatCurrency(venue?.pricePerHour * bookingData?.duration)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Taxa de serviço (5%)</span>
                    <span className="font-data">
                      {formatCurrency(venue?.pricePerHour * bookingData?.duration * 0.05)}
                    </span>
                  </div>
                  <div className="border-t border-border pt-2 flex justify-between font-medium">
                    <span>Total</span>
                    <span className="font-data text-primary">
                      {formatCurrency(calculateTotal())}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Payment */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-3">
                  Método de Pagamento
                </label>
                <div className="space-y-3">
                  {paymentMethods?.map((method) => (
                    <div
                      key={method?.value}
                      className={`border rounded-lg p-4 cursor-pointer transition-standard ${
                        bookingData?.paymentMethod === method?.value
                          ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                      }`}
                      onClick={() => handleInputChange('paymentMethod', method?.value)}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-4 h-4 rounded-full border-2 ${
                          bookingData?.paymentMethod === method?.value
                            ? 'border-primary bg-primary' :'border-muted-foreground'
                        }`}>
                          {bookingData?.paymentMethod === method?.value && (
                            <div className="w-2 h-2 bg-white rounded-full m-0.5"></div>
                          )}
                        </div>
                        <Icon name={method?.icon} size={20} className="text-muted-foreground" />
                        <span className="font-medium">{method?.label}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* PIX Instructions */}
              {bookingData?.paymentMethod === 'pix' && (
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="Info" size={16} className="text-primary mt-0.5" />
                    <div>
                      <h4 className="font-medium text-primary mb-1">Pagamento via PIX</h4>
                      <p className="text-sm text-muted-foreground">
                        Após confirmar a reserva, você receberá um código PIX para pagamento. 
                        A quadra será reservada por 15 minutos para conclusão do pagamento.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <Checkbox
                label="Aceito os termos e condições"
                description="Li e concordo com os termos de uso e política de cancelamento"
                checked={bookingData?.agreesToTerms}
                onChange={(e) => handleInputChange('agreesToTerms', e?.target?.checked)}
                required
              />
            </div>
          )}

          {/* Step 3: Confirmation */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="CheckCircle" size={32} className="text-success" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  Confirmar Reserva
                </h3>
                <p className="text-muted-foreground">
                  Revise os detalhes da sua reserva antes de finalizar
                </p>
              </div>

              {/* Booking Summary */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Quadra:</span>
                  <span className="font-medium">{venue?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Data:</span>
                  <span className="font-medium">
                    {new Date(bookingData.date)?.toLocaleDateString('pt-BR')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Horário:</span>
                  <span className="font-medium">
                    {bookingData?.startTime} ({bookingData?.duration}h)
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Jogadores:</span>
                  <span className="font-medium">{bookingData?.playerCount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Pagamento:</span>
                  <span className="font-medium">
                    {paymentMethods?.find(m => m?.value === bookingData?.paymentMethod)?.label}
                  </span>
                </div>
                <div className="border-t border-border pt-3 flex justify-between font-medium text-lg">
                  <span>Total:</span>
                  <span className="font-data text-primary">
                    {formatCurrency(calculateTotal())}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="flex space-x-2">
            {step > 1 && (
              <Button
                variant="outline"
                onClick={handlePrevStep}
                iconName="ChevronLeft"
                iconPosition="left"
                iconSize={16}
              >
                Voltar
              </Button>
            )}
          </div>
          
          <div className="flex space-x-2">
            <Button
              variant="ghost"
              onClick={onClose}
            >
              Cancelar
            </Button>
            
            {step < 3 ? (
              <Button
                variant="default"
                onClick={handleNextStep}
                disabled={!isStepValid()}
                iconName="ChevronRight"
                iconPosition="right"
                iconSize={16}
              >
                Continuar
              </Button>
            ) : (
              <Button
                variant="default"
                onClick={handleConfirmBooking}
                loading={isLoading}
                iconName="Check"
                iconPosition="left"
                iconSize={16}
              >
                Confirmar Reserva
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingModal;